import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, IconButton, Typography, Tooltip, Stack, Chip, Paper, ClickAwayListener, CircularProgress } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import { apiFetch } from 'utils/api';

export default function PromptTemplateSelector({ selectedTemplate, onSelect, chipOnly = false }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/prompt-templates');
      setTemplates(data?.templates || []);
    } catch (err) {
      console.error('Failed to load prompt templates', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open, fetchTemplates]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleSelect = (template) => {
    onSelect(template);
    setOpen(false);
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onSelect(null);
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }} ref={anchorRef}>
      {/* ── Active template chip ── */}
      {selectedTemplate && (
        <Chip
          icon={<AutoFixHighIcon sx={{ fontSize: '13px !important', ml: '4px !important' }} />}
          label={selectedTemplate.name}
          size="small"
          onDelete={handleClear}
          deleteIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
          onClick={chipOnly ? undefined : handleToggle}
          sx={{
            mr: chipOnly ? 0 : 0.5,
            maxWidth: 180,
            height: 28,
            fontSize: 12,
            fontWeight: 500,
            borderRadius: '8px',
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            '& .MuiChip-label': { px: 0.75 },
            '& .MuiChip-icon': { color: theme.palette.primary.main },
            '& .MuiChip-deleteIcon': {
              color: alpha(theme.palette.primary.main, 0.5),
              '&:hover': { color: theme.palette.primary.main }
            },
            transition: 'all 0.15s'
          }}
        />
      )}

      {/* ── Trigger button (hidden in chipOnly mode) ── */}
      {!chipOnly && (
        <Tooltip title={selectedTemplate ? 'Change prompt template' : 'Attach a prompt template'} arrow>
          <IconButton
            onClick={handleToggle}
            size="small"
            sx={{
              color: selectedTemplate ? theme.palette.primary.main : theme.palette.text.disabled,
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.06)
              }
            }}
          >
            <AutoFixHighIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* ── Dropdown ── */}
      {open && (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper
            elevation={12}
            sx={{
              position: 'absolute',
              bottom: '100%',
              left: 0,
              mb: 1,
              width: 300,
              maxHeight: 340,
              overflowY: 'auto',
              borderRadius: '12px',
              zIndex: 1300,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            {/* Header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Prompt Templates
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select a template to attach to this message
              </Typography>
            </Box>

            {/* Loading */}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* Empty */}
            {!loading && templates.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No templates yet
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Create templates in Internal Kit Corner
                </Typography>
              </Box>
            )}

            {/* Template list */}
            {!loading && templates.length > 0 && (
              <Stack sx={{ py: 0.5 }}>
                {/* Clear selection option */}
                {selectedTemplate && (
                  <Box
                    onClick={() => handleSelect(null)}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.06) }
                    }}
                  >
                    <Typography variant="body2" color="error" sx={{ fontWeight: 500, fontSize: 13 }}>
                      Remove template
                    </Typography>
                  </Box>
                )}

                {templates.map((t) => {
                  const isActive = selectedTemplate?.id === t.id;
                  return (
                    <Box
                      key={t.id}
                      onClick={() => handleSelect(t)}
                      sx={{
                        px: 2,
                        py: 1.2,
                        cursor: 'pointer',
                        backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.06)
                        }
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {t.is_favorite && <StarIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />}
                        <Typography variant="body2" sx={{ fontWeight: isActive ? 600 : 500, fontSize: 13 }} noWrap>
                          {t.name}
                        </Typography>
                      </Stack>
                      {t.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.2 }}>
                          {t.description}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
}
